import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, Select } from '@devkit/ui';
import { ArrowRight, Plus, Minus } from 'lucide-react';
import { trackToolExecute, trackClear } from '@devkit/analytics';

const viewModeOptions = [
  { value: 'side-by-side', label: 'Side by Side' },
  { value: 'inline', label: 'Inline' },
];

interface DiffLine {
  type: 'unchanged' | 'added' | 'removed';
  content: string;
  lineNumber?: number;
}

export default function TextDiffPage() {
  const [text1, setText1] = useState('Hello World\nThis is the original text.\nIt has multiple lines.\nLast line here.');
  const [text2, setText2] = useState('Hello Universe\nThis is the modified text.\nIt has multiple lines.\nThis is a new line.');
  const [viewMode, setViewMode] = useState('side-by-side');
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);
  const [ignoreCase, setIgnoreCase] = useState(false);

  const computeDiff = useCallback((): { lines1: DiffLine[]; lines2: DiffLine[]; stats: { added: number; removed: number; unchanged: number } } => {
    trackToolExecute('text-diff', 'compute');
    const lines1 = text1.split('\n');
    const lines2 = text2.split('\n');

    let processed1 = ignoreCase ? lines1.map(l => l.toLowerCase()) : lines1;
    let processed2 = ignoreCase ? lines2.map(l => l.toLowerCase()) : lines2;

    if (ignoreWhitespace) {
      processed1 = processed1.map(l => l.trim());
      processed2 = processed2.map(l => l.trim());
    }

    const diffLines1: DiffLine[] = [];
    const diffLines2: DiffLine[] = [];
    let stats = { added: 0, removed: 0, unchanged: 0 };

    const maxLen = Math.max(processed1.length, processed2.length);
    let lineNum1 = 1;
    let lineNum2 = 1;

    for (let i = 0; i < maxLen; i++) {
      const l1 = processed1[i];
      const l2 = processed2[i];

      if (l1 === undefined) {
        diffLines2.push({ type: 'added', content: lines2[i], lineNumber: lineNum2++ });
        stats.added++;
      } else if (l2 === undefined) {
        diffLines1.push({ type: 'removed', content: lines1[i], lineNumber: lineNum1++ });
        stats.removed++;
      } else if (l1 === l2) {
        diffLines1.push({ type: 'unchanged', content: lines1[i], lineNumber: lineNum1++ });
        diffLines2.push({ type: 'unchanged', content: lines2[i], lineNumber: lineNum2++ });
        stats.unchanged++;
      } else {
        diffLines1.push({ type: 'removed', content: lines1[i], lineNumber: lineNum1++ });
        diffLines2.push({ type: 'added', content: lines2[i], lineNumber: lineNum2++ });
        stats.removed++;
        stats.added++;
      }
    }

    return { lines1: diffLines1, lines2: diffLines2, stats };
  }, [text1, text2, ignoreWhitespace, ignoreCase]);

  const diff = computeDiff();

  const clearAll = () => {
    setText1('');
    setText2('');
    trackClear('text-diff');
  };

  return (
    <ToolLayout slug="text-diff">
      <Card>
        <CardContent className="p-6">
          {/* Options */}
          <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400">View:</label>
              <Select
                options={viewModeOptions}
                value={viewMode}
                onChange={setViewMode}
                className="w-40"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={ignoreWhitespace}
                onChange={(e) => setIgnoreWhitespace(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-surface text-primary focus:ring-primary"
              />
              <span className="text-slate-300">Ignore whitespace</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={ignoreCase}
                onChange={(e) => setIgnoreCase(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-surface text-primary focus:ring-primary"
              />
              <span className="text-slate-300">Ignore case</span>
            </label>
          </div>

          {/* Stats */}
          {text1 || text2 ? (
            <div className="flex gap-4 mb-4 p-3 bg-surface rounded-lg">
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-slate-400">{diff.stats.added} added</span>
              </div>
              <div className="flex items-center gap-2">
                <Minus className="w-4 h-4 text-red-500" />
                <span className="text-sm text-slate-400">{diff.stats.removed} removed</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">{diff.stats.unchanged} unchanged</span>
              </div>
            </div>
          ) : null}

          {/* Side by Side View */}
          {viewMode === 'side-by-side' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-300">Original Text</label>
                  <span className="text-xs text-slate-500">Left</span>
                </div>
                <textarea
                  value={text1}
                  onChange={(e) => setText1(e.target.value)}
                  placeholder="Paste original text..."
                  className="w-full h-64 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-300">Modified Text</label>
                  <span className="text-xs text-slate-500">Right</span>
                </div>
                <textarea
                  value={text2}
                  onChange={(e) => setText2(e.target.value)}
                  placeholder="Paste modified text..."
                  className="w-full h-64 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
                />
              </div>
            </div>
          )}

          {/* Inline View */}
          {viewMode === 'inline' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Original Text</label>
                <textarea
                  value={text1}
                  onChange={(e) => setText1(e.target.value)}
                  placeholder="Paste original text..."
                  className="w-full h-40 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
                />
              </div>
              <div className="flex justify-center">
                <ArrowRight className="w-5 h-5 text-slate-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Modified Text</label>
                <textarea
                  value={text2}
                  onChange={(e) => setText2(e.target.value)}
                  placeholder="Paste modified text..."
                  className="w-full h-40 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mt-4">
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          {/* Diff Output */}
          {text1 || text2 ? (
            <div className="mt-6 pt-6 border-t border-slate-700">
              <h3 className="text-sm font-medium text-slate-300 mb-3">Differences</h3>
              
              {viewMode === 'side-by-side' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-surface border border-slate-700 rounded-lg font-mono text-sm overflow-auto max-h-96">
                    {diff.lines1.map((line, i) => (
                      <div
                        key={i}
                        className={`px-2 py-0.5 ${
                          line.type === 'removed'
                            ? 'bg-red-500/20 text-red-300'
                            : line.type === 'added'
                            ? 'bg-emerald-500/10 text-slate-500'
                            : 'text-slate-300'
                        }`}
                      >
                        <span className="inline-block w-8 text-slate-500 select-none">
                          {line.lineNumber}
                        </span>
                        <span>{line.content || ' '}</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 bg-surface border border-slate-700 rounded-lg font-mono text-sm overflow-auto max-h-96">
                    {diff.lines2.map((line, i) => (
                      <div
                        key={i}
                        className={`px-2 py-0.5 ${
                          line.type === 'added'
                            ? 'bg-emerald-500/20 text-emerald-300'
                            : line.type === 'removed'
                            ? 'bg-red-500/10 text-slate-500'
                            : 'text-slate-300'
                        }`}
                      >
                        <span className="inline-block w-8 text-slate-500 select-none">
                          {line.lineNumber}
                        </span>
                        <span>{line.content || ' '}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-surface border border-slate-700 rounded-lg font-mono text-sm overflow-auto max-h-96">
                  {diff.lines1.map((line, i) => (
                    <div key={`1-${i}`} className="flex">
                      <span className="inline-block w-8 text-slate-500 select-none">{line.lineNumber}</span>
                      <span
                        className={`flex-1 px-2 ${
                          line.type === 'removed' ? 'bg-red-500/20 text-red-300' : 'text-slate-500'
                        }`}
                      >
                        {line.content || ' '}
                      </span>
                    </div>
                  ))}
                  {diff.lines2
                    .filter((l) => l.type === 'added')
                    .map((line, i) => (
                      <div key={`2-${i}`} className="flex">
                        <span className="inline-block w-8 text-slate-500 select-none">{line.lineNumber}</span>
                        <span className="flex-1 px-2 bg-emerald-500/20 text-emerald-300">
                          {line.content || ' '}
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
